import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, Stack, Typography } from "@mui/material";
import { useQuery } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { MONITORING_GET_DASHBOARD } from "@/queries/MonitoringGetDashboard";
import { EMonitoringCapability, EMonitoringViewMode, EScopeSelector, } from "./types";
import { layoutCells } from "./lib/layout";
import { BUSY_RETRY_MS, EMonitoringErrorCode, monitoringErrorCode, monitoringErrorMessage, } from "./lib/errors";
import { dashboardSelectorValues } from "./lib/selectors";
import { monitoringRefreshMs } from "./lib/refresh";
import { parseDashboard, parseWidgets } from "./lib/parseDefinitions";
import { PINNED_BY_POST, scopeLabel } from "./lib/scopeLabel";
import { useMonitoring } from "./MonitoringProvider";
import { useMonitoringPolling } from "./useMonitoringPolling";
import { MonitoringHeader } from "./MonitoringHeader";
import { MonitoringSelectors } from "./MonitoringSelectors";
import { MonitoringWidgetGrid } from "./MonitoringWidgetGrid";
import { MonitoringExportDialog } from "./MonitoringExportDialog";
import { MonitoringFooter } from "./MonitoringFooter";
/** The scope a request carries: the dashboard's selectors only, with values the viewer may choose. */
function effectiveScope(chosen, selectors, options, pinnedPost) {
    var scope = {};
    for (var _i = 0, selectors_1 = selectors; _i < selectors_1.length; _i++) {
        var selector = selectors_1[_i];
        var key = chosen[selector];
        if (!key || (pinnedPost && PINNED_BY_POST.has(selector)))
            continue;
        if (!hasOption(options, selector, key))
            continue;
        scope[selector] = key;
    }
    return scope;
}
function hasOption(options, selector, key) {
    var list = selector === EScopeSelector.REGION
        ? options.regions
        : selector === EScopeSelector.POST
            ? options.posts
            : options.countries;
    return list.some(function (option) { return option.key === key; });
}
export function MonitoringDashboard(_a) {
    var _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p;
    var electionEventId = _a.electionEventId, electionId = _a.electionId, dashboards = _a.dashboards, presetTitle = _a.presetTitle, configure = _a.configure, refreshSeconds = _a.refreshSeconds;
    var t = useTranslation().t;
    var _q = useMonitoring(), state = _q.state, selectDashboard = _q.selectDashboard, setScope = _q.setScope, actions = _q.actions, widgetValues = _q.widgetValues;
    var _r = useState(false), exporting = _r[0], setExporting = _r[1];
    var dashboardId = dashboards.some(function (dashboard) { return dashboard.id === state.dashboardId; })
        ? state.dashboardId
        : dashboards[0].id;
    useEffect(function () {
        if (state.dashboardId !== dashboardId)
            selectDashboard(dashboardId);
    }, [state.dashboardId, dashboardId, selectDashboard]);
    var _s = useQuery(MONITORING_GET_DASHBOARD, {
        variables: { electionEventId: electionEventId, electionId: electionId !== null && electionId !== void 0 ? electionId : null, dashboardId: dashboardId },
    }), data = _s.data, error = _s.error, refetch = _s.refetch;
    var reload = useCallback(function () { return void refetch().catch(function () { return undefined; }); }, [refetch]);
    // A poll that fails keeps this dashboard shown; the next one asks again.
    var shown = useRef(null);
    if (data)
        shown.current = { dashboardId: dashboardId, data: data };
    var kept = error && ((_b = shown.current) === null || _b === void 0 ? void 0 : _b.dashboardId) === dashboardId ? shown.current.data : undefined;
    // As often as the server counts (every 30 s by default) while the tab is
    // shown and nobody is editing, and at once when the tab is shown again.
    // Charts are drawn again only when the snapshot the poll reports is a new
    // one; a widget that failed asks again on each poll.
    var refreshMs = monitoringRefreshMs((_e = (_d = (data !== null && data !== void 0 ? data : (_c = shown.current) === null || _c === void 0 ? void 0 : _c.data)) === null || _d === void 0 ? void 0 : _d.monitoringGetDashboard.refresh_seconds) !== null && _e !== void 0 ? _e : refreshSeconds);
    var _t = useState(0), pollCount = _t[0], setPollCount = _t[1];
    useMonitoringPolling({
        active: state.mode === EMonitoringViewMode.VIEW,
        intervalMs: refreshMs,
        onPoll: function () {
            reload();
            setPollCount(function (count) { return count + 1; });
        },
    });
    var busy = !data && !kept && monitoringErrorCode(error) === EMonitoringErrorCode.BUSY
        ? error
        : undefined;
    useEffect(function () {
        // Each busy answer waits, then asks again.
        if (!busy)
            return;
        var timer = window.setTimeout(reload, BUSY_RETRY_MS);
        return function () { return window.clearTimeout(timer); };
    }, [busy, reload]);
    var response = (_f = (data !== null && data !== void 0 ? data : kept)) === null || _f === void 0 ? void 0 : _f.monitoringGetDashboard;
    var parsed = useMemo(function () {
        return response
            ? {
                dashboard: parseDashboard(response.dashboard),
                widgets: parseWidgets(response.widgets),
            }
            : null;
    }, [response]);
    if (!response) {
        if (error) {
            return (_jsxs(Alert, { severity: "error", action: _jsx(Button, { color: "inherit", size: "small", onClick: reload, children: t("monitoring.retry") }), children: [t("monitoring.dashboardFailed"), monitoringErrorCode(error) ? " ".concat(t(monitoringErrorMessage(error))) : null] }));
        }
        return (_jsx(Box, { sx: { display: "flex", justifyContent: "center", py: 4 }, children: _jsx(CircularProgress, { "aria-label": t("monitoring.loading") }) }));
    }
    if (!(parsed === null || parsed === void 0 ? void 0 : parsed.dashboard.ok)) {
        return (_jsx(Alert, { severity: "error", children: t("monitoring.dashboardInvalid", {
                problem: parsed && !parsed.dashboard.ok ? parsed.dashboard.problem : "",
            }) }));
    }
    var dashboard = parsed.dashboard.value;
    var selectors = (_g = dashboard.selectors) !== null && _g !== void 0 ? _g : [];
    var scope = effectiveScope(state.dashboardValues, selectors, response.scope_options, response.pinned_post);
    var label = scopeLabel({
        scope: scope,
        selectors: selectors,
        options: response.scope_options,
        settings: response.settings,
        t: t,
        restricted: response.restricted,
        pinnedPost: response.pinned_post,
    });
    var cells = layoutCells(dashboard, parsed.widgets);
    var snapshot = (_h = response.snapshot) !== null && _h !== void 0 ? _h : null;
    var eventDays = (_j = response.event_days) !== null && _j !== void 0 ? _j : [];
    var context = {
        electionEventId: electionEventId,
        electionId: electionId,
        dashboardId: dashboardId,
        scope: scope,
        scopeLabel: label,
        snapshot: snapshot,
        sources: (_k = response.sources) !== null && _k !== void 0 ? _k : {},
        timeZone: (_l = response.time_zone) !== null && _l !== void 0 ? _l : "UTC",
        eventDays: eventDays,
        configVersion: "".concat(response.dashboard_revision, "/").concat((_o = (_m = response.theme) === null || _m === void 0 ? void 0 : _m.revision) !== null && _o !== void 0 ? _o : "", "/").concat(response.settings_revision),
        pollCount: pollCount,
        onSnapshotPruned: reload,
        configure: configure,
    };
    var onEditDashboard = configure === EMonitoringCapability.GRANTED && actions.onEditDashboard
        ? function () { var _a; return (_a = actions.onEditDashboard) === null || _a === void 0 ? void 0 : _a.call(actions, dashboardId); }
        : undefined;
    return (_jsxs(Stack, { spacing: 2, sx: { py: 2 }, children: [_jsx(MonitoringHeader, { presetTitle: presetTitle, dashboards: dashboards, dashboardId: dashboardId, onSelectDashboard: selectDashboard, snapshot: snapshot, timeZone: context.timeZone, refreshMs: refreshMs, onRefresh: reload, onExport: snapshot ? function () { return setExporting(true); } : undefined, onEditDashboard: onEditDashboard, children: _jsx(MonitoringSelectors, { selectors: selectors, scope: scope, onChange: setScope, options: response.scope_options, settings: response.settings, restricted: response.restricted, pinnedPost: response.pinned_post }) }), _jsxs(Box, { children: [dashboard.section ? (_jsx(Typography, { variant: "overline", component: "p", color: "text.secondary", children: dashboard.section })) : null, _jsx(Typography, { variant: "h5", component: "h2", sx: { color: "brandColor" }, children: dashboard.title }), dashboard.description ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: dashboard.description })) : null] }), _jsx(MonitoringWidgetGrid, { cells: cells, context: context }), _jsx(MonitoringFooter, { scopeLabel: label, snapshot: snapshot, timeZone: context.timeZone, requirements: (_p = dashboard.requirements) !== null && _p !== void 0 ? _p : [] }), snapshot ? (_jsx(MonitoringExportDialog, { open: exporting, onClose: function () { return setExporting(false); }, title: dashboard.title, scope: label, timeZone: context.timeZone, onSnapshotPruned: reload, widgets: cells.flatMap(function (cell) { return (cell.widget ? [cell.widget] : []); }), target: {
                    electionEventId: electionEventId,
                    electionId: electionId !== null && electionId !== void 0 ? electionId : null,
                    dashboardId: dashboardId,
                    widgetId: null,
                    scope: scope,
                    selectorValues: {},
                    widgetSelectorValues: dashboardSelectorValues(cells, function (cell) { return widgetValues(cell); }, { event_days: eventDays }),
                    snapshotRevision: snapshot.revision,
                } })) : null] }));
}
